import { Anchor, FileButton, SegmentedControl, Text } from "@mantine/core";
import { FolderUp } from "lucide-react";
import { useEffect, useRef, useState, type DragEvent } from "react";

import type { CatalogAudioModel, Voice } from "../../api/generated/model";
import { readDuration } from "./audio-file";
import { AudioRecorder } from "./AudioRecorder";
import { DesignVoice } from "./DesignVoice";
import { UPLOAD_HINT, VOICE_ACCEPT, voiceProblems } from "./limits";
import { useVoiceSave } from "./use-voice-save";
import { VoicePreview } from "./VoicePreview";
import classes from "./voices.module.css";

export type CreateVoiceTab = "record" | "upload" | "design";

/** Live's tab words in the picker (`voice-select/constants.ts`). */
const TABS: { value: CreateVoiceTab; label: string }[] = [
  { value: "record", label: "Record" },
  { value: "upload", label: "Upload" },
  { value: "design", label: "Design" },
];

export interface CreateVoiceProps {
  teamId: string;
  brandId: string;
  /** The catalog's voice design model, or none: Design is Coming soon. */
  designModel: CatalogAudioModel | undefined;
  /** A voice made and saved: live chooses it at once. */
  onCreated: (voice: Voice) => void;
  /** The tabs, in their words, where a form has its own (Your Personal Clone's two). */
  tabs?: readonly { value: CreateVoiceTab; label: string }[];
}

/** A file to make a voice of, with what live says is wrong with it. */
interface Taken {
  file: File;
  url: string;
  problems: string[];
}

/**
 * Live's Create Voice (`voice-create`, captures L02b to L02d): Record, Upload and Design. A
 * recording or a file shows in live's preview with its problems, its name and Save; switching
 * tabs drops it, as live's does.
 */
export function CreateVoice({ teamId, brandId, designModel, onCreated, tabs = TABS }: CreateVoiceProps) {
  const [tab, setTab] = useState<CreateVoiceTab>("record");
  const [taken, setTaken] = useState<Taken | null>(null);
  const drop = () =>
    setTaken((current) => {
      if (current !== null) URL.revokeObjectURL(current.url);
      return null;
    });
  const saver = useVoiceSave(teamId, brandId, (voice) => {
    drop();
    onCreated(voice);
  });
  // A file read for its length, then shown, problems and all (live shows an invalid one too).
  const take = async (file: File | null) => {
    if (file === null) return;
    const seconds = await readDuration(file);
    const url = URL.createObjectURL(file);
    setTaken((current) => {
      if (current !== null) URL.revokeObjectURL(current.url);
      return { file, url, problems: voiceProblems(file, seconds) };
    });
  };
  const takenUrl = useRef<string | null>(null);
  useEffect(() => {
    takenUrl.current = taken?.url ?? null;
  });
  useEffect(() => () => {
    if (takenUrl.current !== null) URL.revokeObjectURL(takenUrl.current);
  }, []);
  return (
    <div className={classes.create}>
      <SegmentedControl
        fullWidth
        data={[...tabs]}
        value={tab}
        aria-label="Create Voice"
        onChange={(next) => {
          drop();
          setTab(next as CreateVoiceTab);
        }}
      />
      {taken !== null ? (
        <VoicePreview
          url={taken.url}
          label={taken.file.name}
          problems={taken.problems}
          onReset={drop}
          onSave={(name) => void saver.save(taken.file, name)}
          saving={saver.pending}
          progress={saver.progress}
          failure={saver.problem}
        />
      ) : tab === "record" ? (
        <RecordPane onRecorded={(file) => void take(file)} />
      ) : tab === "upload" ? (
        <UploadPane onFile={(file) => void take(file)} />
      ) : (
        <DesignVoice teamId={teamId} brandId={brandId} model={designModel} onCreated={onCreated} />
      )}
    </div>
  );
}

/** Live's Record (capture L02b): Create Voice's words over the shared recorder. */
function RecordPane({ onRecorded }: { onRecorded: (file: File) => void }) {
  return (
    <AudioRecorder
      title="Record your voice clearly"
      description="You can read any text you like — just make sure your voice is easy to understand."
      hint="15 sec – 1 min"
      onRecorded={onRecorded}
    />
  );
}

/** Live's Upload (capture L02c): the dashed drop zone, "Browse files" and live's rule. */
function UploadPane({ onFile }: { onFile: (file: File) => void }) {
  const [over, setOver] = useState(false);
  const files = {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      setOver(true);
    },
    onDragLeave: () => setOver(false),
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setOver(false);
      // Live takes the first file and lets its check say what is wrong.
      const file = event.dataTransfer.files[0];
      if (file !== undefined) onFile(file);
    },
  };
  return (
    <section className={classes.drop} data-over={over || undefined} aria-label="Upload a voice" {...files}>
      <FolderUp size={40} strokeWidth={1.5} aria-hidden />
      <Text size="sm">Drag your file(s) here</Text>
      <Text className={classes.or}>OR</Text>
      <FileButton
        accept={VOICE_ACCEPT}
        inputProps={{ "aria-label": "Choose a voice file" }}
        onChange={(file) => (file === null ? undefined : onFile(file))}
      >
        {(props) => (
          <Anchor component="button" type="button" {...props}>
            Browse files
          </Anchor>
        )}
      </FileButton>
      <Text className={classes.small}>{UPLOAD_HINT}</Text>
    </section>
  );
}
